import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import {
  Card,
  Button,
  Table,
  Modal,
  Form,
  Input,
  Select,
  Spin,
  message,
  Tag,
  Typography,
  Space,
  Tabs,
  Image
} from 'antd';
import { PlusCircleOutlined } from '@ant-design/icons';
import { getProjectTypes } from '../../api/project_type';
// import { getGHGMethodologies } from '../../api/ghg';
import {
  getCompassModules,
  getCompassUpdates,
  createCompassModule,
  updateCompassModule
} from '../../api/compass_modules';
import { Link } from 'react-router-dom';
import dayjs from 'dayjs';
import { ASSET_BASE_URL } from '../../api/axios';

const { Title } = Typography;

const CompassModules: React.FC = () => {
  const queryClient = useQueryClient();
  const [form] = Form.useForm();
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [editingModule, setEditingModule] = useState<any>(null);

  const { data: modules, isLoading: isModulesLoading } = useQuery(
    'compassModules',
    getCompassModules
  );
  const { data: projectTypes } = useQuery('projectTypes', getProjectTypes);
  // const { data: ghgMethodologies } = useQuery('ghgMethodologies', getGHGMethodologies);
  const [updatesPage, setUpdatesPage] = useState(1);
  const [updatesLimit, setUpdatesLimit] = useState(10);
  const { data: updatesData, isLoading: isUpdatesLoading } = useQuery(
    ['compassUpdates', updatesPage, updatesLimit],
    () => getCompassUpdates({ page: updatesPage, limit: updatesLimit })
  );
  const { mutate: createModule, isLoading: isCreating } = useMutation(createCompassModule, {
    onSuccess: () => {
      queryClient.invalidateQueries('compassModules');
      message.success('Module created successfully!');
      closeModal();
    },
    onError: (error: any) => {
      message.error('Failed to create module: ' + error.message);
    }
  });

  const { mutate: updateModule, isLoading: isUpdating } = useMutation(
    ({ id, data }: { id: string; data: any }) => updateCompassModule(id, data),
    {
      onSuccess: () => {
        queryClient.invalidateQueries('compassModules');
        message.success('Module updated successfully!');
        closeModal();
      },
      onError: (error: any) => {
        message.error('Failed to update module: ' + error.message);
      }
    }
  );

  const openModal = (module?: any) => {
    if (module) {
      setEditingModule(module);
      form.setFieldsValue({
        name: module.display_name,
        description: module.description,
        projectTypes: module.project_types.map((pt: any) => pt.project_type.id),
        methodologies: module.methodologies.map((m: any) => m.ghg_methodology.id)
      });
    } else {
      setEditingModule(null);
      form.resetFields();
    }
    setIsModalVisible(true);
  };

  const closeModal = () => {
    setIsModalVisible(false);
    setEditingModule(null);
    form.resetFields();
  };

  const handleSave = async () => {
    try {
      const values = await form.validateFields();

      if (
        (!values.projectTypes || values.projectTypes.length === 0) &&
        (!values.methodologies || values.methodologies.length === 0)
      ) {
        message.error('Please select at least one Project Type.');
        return;
      }

      const payload = {
        display_name: values.name,
        description: values.description,
        projectTypeIds: values.projectTypes,
        methodologyIds: values.methodologies
      };

      if (editingModule) {
        updateModule({ id: editingModule.id, data: payload });
      } else {
        createModule(payload);
      }
    } catch (err) {
      console.log('Validation Failed:', err);
    }
  };

  const filteredProjectTypes = projectTypes?.response?.filter(
    (pt: any) => pt.name.toLowerCase() !== 'orphan'
  );

  const columns = [
    {
      title: 'Name',
      dataIndex: 'display_name',
      key: 'name',
      render: (name: string) => <span style={{ color: '#1a202c', fontWeight: 500 }}>{name}</span>
    },
    {
      title: 'Subtitle',
      dataIndex: 'description',
      key: 'description',
      render: (desc: string) => <span style={{ color: '#4a5568' }}>{desc || '—'}</span>
    },
    {
      title: 'Project Types',
      dataIndex: 'project_types',
      key: 'projectTypes',
      render: (types: any[]) =>
        types.map((t) => (
          <Tag color="blue" key={t.project_type.id}>
            {t.project_type.display_name}
          </Tag>
        ))
    },
    {
      title: 'Methodologies',
      dataIndex: 'methodologies',
      key: 'methodologies',
      render: (methods: any[]) =>
        methods.map((m) => (
          <Tag color="purple" key={m.ghg_methodology.id}>
            {m.ghg_methodology.display_name}
          </Tag>
        ))
    },
    {
      title: 'Actions',
      key: 'actions',
      render: ({ id }) => (
        <Space size="middle" onClick={(e) => e.stopPropagation()}>
          <Link to={`/compass/${id}`}>
            <Button>Catcher</Button>
          </Link>
        </Space>
      )
    }
  ];

  const updateColumns = [
    {
      title: 'Title',
      dataIndex: 'name',
      key: 'name',
      // image and total
      render: (name: string, record: any) => (
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <Image
            src={`${ASSET_BASE_URL}/${record.image}`}
            alt="update"
            style={{
              height: 50,
              width: 50,
              objectFit: 'cover',
              background: '#eee',
              borderRadius: '5px'
            }}
            fallback={'/images/img/fallback.png'}
          />
          <span style={{ color: '#1a202c', fontWeight: 500 }}>{name}</span>
        </div>
      )
    },
    {
      title: 'Created At',
      dataIndex: 'created_at',
      key: 'created_at',
      render: (date: string) => dayjs(date).format('YYYY-MM-DD')
    }
  ];

  if (isModulesLoading) {
    return (
      <div
        style={{
          height: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center'
        }}>
        <Spin size="default" />
      </div>
    );
  }

  return (
    <div className="p-6">
      <Card
        bordered={false}
        title={<Title level={4}>Compass</Title>}
        extra={
          <Button
            type="primary"
            icon={<PlusCircleOutlined />}
            size="middle"
            onClick={() => openModal()}
            style={{ borderRadius: 4, padding: '0 16px' }}>
            Create Module
          </Button>
        }>
        <Tabs
          items={[
            {
              key: 'modules',
              label: 'Modules',
              children: (
                <Table
                  dataSource={modules}
                  columns={columns}
                  rowKey="id"
                  pagination={{ pageSize: 10 }}
                  onRow={(record) => ({
                    onClick: () => openModal(record),
                    style: { cursor: 'pointer' }
                  })}
                />
              )
            },
            {
              key: 'updates',
              label: 'Updates',
              children: (
                <Table
                  dataSource={updatesData?.data}
                  loading={isUpdatesLoading}
                  columns={updateColumns}
                  rowKey={(row) => row?.id || `${row?.name}-${row?.created_at}`}
                  pagination={{
                    current: updatesData?.pagination?.page || updatesPage,
                    pageSize: updatesData?.pagination?.limit || updatesLimit,
                    total: updatesData?.pagination?.total || 0,
                    onChange: (page, pageSize) => {
                      setUpdatesPage(page);
                      setUpdatesLimit(pageSize);
                    }
                  }}
                />
              )
            }
          ]}
        />
      </Card>

      <Modal
        title={editingModule ? 'Edit Module' : 'Create Module'}
        open={isModalVisible}
        onOk={handleSave}
        onCancel={closeModal}
        okText={editingModule ? 'Update' : 'Create'}
        confirmLoading={isCreating || isUpdating}
        width={600}
        maskClosable={false}
        styles={{ body: { marginTop: '20px' } }}>
        <Form form={form} layout="vertical">
          <Form.Item
            name="name"
            label="Module Name"
            rules={[{ required: true, message: 'Please enter module name' }]}>
            <Input placeholder="Enter module name" />
          </Form.Item>
          <Form.Item name="description" label="Sub Title">
            <Input.TextArea placeholder="Enter module description" maxLength={50} showCount />
          </Form.Item>
          <Form.Item name="projectTypes" label="Project Types">
            <Select
              mode="multiple"
              placeholder="Select project types"
              options={filteredProjectTypes?.map((type: any) => ({
                value: type.id,
                label: type.display_name
              }))}
            />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default CompassModules;
